import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ComputerIcon from '@mui/icons-material/Computer';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import Chart from 'react-apexcharts';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import {
  SECURITY_SEVERITY_CHART_CARD_MIN_HEIGHT,
  SecurityDashboardCard,
  SecuritySecurityScoreCard,
  SecuritySeverityCompareBarChart,
  SecurityTimelineListItemShell,
  securityDashboardCardBodySx,
} from './shared/securityDashboardCard';
import { SecurityDashboardHeader } from './shared/securityDashboardToolbar';
import { useSecurityDashboardGrid } from './shared/useSecurityDashboardGrid';
import { useWazuhDashboardData } from './shared/useWazuhDashboardData';
import { useSocDashboardPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import {
  formatImpactPercent,
  formatRelativeTime,
  resolveOrganizationName,
  severitySeriesToCounts,
} from './shared/wazuhDashboardUtils';
import type { Column, TableData } from 'src/types/table';

const COLOR_BLUE = '#3182CE';
const COLOR_RED = '#E53E3E';
const COLOR_ORANGE = '#DD6B20';
const SOC_PAGE_SIZE = 5;

const DEFAULT_SEVERITY_COUNTS = [0, 0, 0, 0];

const formatMetricTotal = (value: number | undefined | null): string =>
  value != null ? value.toLocaleString() : '0';

const THREAT_CATEGORY_COLORS = [
  '#3182CE',
  '#38B2AC',
  '#ED8936',
  '#E53E3E',
  '#2C5282',
] as const;

type OrgRow = {
  id: string;
  client: string;
  securityScore: number;
  endpoints: number;
  criticalAlerts: number;
};

type IncidentItem = {
  id: string;
  title: string;
  clientType: string;
  client: string;
  endpoint: string;
  time: string;
};

type CveRow = {
  id: string;
  cveId: string;
  severity: string;
  affectedEndpoints: number;
  title: string;
};

const incidentMutedLabelStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 10,
  fontStyle: 'italic',
  lineHeight: '14px',
  color: '#94A3B8',
};

const incidentValueStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  lineHeight: '16px',
  color: '#64748B',
};

const IncidentListItem: FC<{ item: IncidentItem; isLast: boolean }> = ({ item, isLast }) => (
  <SecurityTimelineListItemShell isLast={isLast}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
      <Typography
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: '18px',
          color: '#1E293B',
        }}
      >
        {item.title}
      </Typography>
      <Typography
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11,
          lineHeight: '16px',
          color: '#94A3B8',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {item.time}
      </Typography>
    </Box>

    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      style={{ gap: 12, marginTop: 4 }}
    >
      <Typography style={incidentMutedLabelStyle}>{item.clientType}</Typography>
      <Typography style={{ ...incidentMutedLabelStyle, flexShrink: 0 }}>Endpoint</Typography>
    </Box>

    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      style={{ gap: 12, marginTop: 2 }}
    >
      <Typography style={incidentValueStyle}>{item.client}</Typography>
      <Typography style={{ ...incidentValueStyle, flexShrink: 0 }}>{item.endpoint}</Typography>
    </Box>
  </SecurityTimelineListItemShell>
);

const SEVERITY_BADGE_STYLES: Record<string, { color: string; bg: string }> = {
  critical: { color: '#DC2626', bg: '#FEE2E2' },
  high: { color: '#DD6B20', bg: '#FEEBC8' },
  medium: { color: '#B45309', bg: '#FEF3C7' },
  low: { color: '#15803D', bg: '#DCFCE7' },
};

const getSeverityBadgeStyle = (severity: string) =>
  SEVERITY_BADGE_STYLES[severity?.toLowerCase()] ?? SEVERITY_BADGE_STYLES.critical;

const DONUT_CHART_SIZE = 148;

/** Min width for side-by-side donut + legend; below this the legend wraps below the chart. */
const THREAT_LEGEND_MIN_WIDTH = 220;

type ThreatDonutChartProps = {
  labels: string[];
  series: number[];
  total: number;
  loading?: boolean;
};

const EMPTY_DONUT_COLOR = '#E2E8F0';

const ThreatDonutChart: FC<ThreatDonutChartProps> = ({ labels, series, total, loading }) => {
  const formattedTotal = total.toLocaleString();
  const isEmpty = series.length === 0 || series.every((value) => !value);

  const chartLabels = isEmpty ? ['No data'] : labels;
  const chartSeries = isEmpty ? [1] : series;
  const chartColors = isEmpty
    ? [EMPTY_DONUT_COLOR]
    : [...THREAT_CATEGORY_COLORS].slice(0, labels.length);

  const options = useMemo(
    () => ({
      chart: {
        type: 'donut' as const,
        toolbar: { show: false },
        animations: { enabled: true },
      },
      labels: chartLabels,
      colors: chartColors,
      legend: { show: false },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: '72%',
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: '20px',
                fontWeight: 700,
                color: '#1E293B',
                fontFamily: 'Inter, sans-serif',
                formatter: () => formattedTotal,
              },
              total: {
                show: true,
                showAlways: true,
                label: 'TOTAL',
                fontSize: '9px',
                fontWeight: 600,
                color: '#94A3B8',
                fontFamily: 'Inter, sans-serif',
                formatter: () => formattedTotal,
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      tooltip: { enabled: !isEmpty, theme: 'light' as const },
      states: isEmpty
        ? {
          hover: { filter: { type: 'none' as const } },
          active: { filter: { type: 'none' as const } },
        }
        : undefined,
    }),
    [formattedTotal, chartLabels, chartColors, isEmpty]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...securityDashboardCardBodySx,
        width: '100%',
        boxSizing: 'border-box',
        flex: '0 1 auto',
        minHeight: 'min-content',
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="center"
        style={{ gap: 16, minWidth: 0, width: '100%' }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            flex: '0 0 auto',
            width: DONUT_CHART_SIZE,
          }}
        >
          <Chart
            options={options}
            series={chartSeries}
            type="donut"
            height={DONUT_CHART_SIZE}
            width={DONUT_CHART_SIZE}
          />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          style={{
            flex: `1 1 ${THREAT_LEGEND_MIN_WIDTH}px`,
            maxWidth: '100%',
            gap: 8,
            paddingBottom: 2,
            boxSizing: 'border-box',
          }}
        >
          {isEmpty ? (
            <Typography
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 12,
                color: '#94A3B8',
                textAlign: 'center',
              }}
            >
              No threat categories in this range
            </Typography>
          ) : null}
          {labels.map((label, index) => (
            <Box
              key={label}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ gap: 12, minWidth: 0 }}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 0, flex: 1 }}>
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: THREAT_CATEGORY_COLORS[index],
                    flexShrink: 0,
                  }}
                />
                <Typography
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 11,
                    lineHeight: '16px',
                    color: '#64748B',
                  }}
                >
                  {label}
                </Typography>
              </Box>
              <Typography
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: '16px',
                  color: '#1E293B',
                  flexShrink: 0,
                }}
              >
                {series[index]?.toLocaleString() ?? '0'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const SecurityScoreBar: FC<{ score: number }> = ({ score }) => (
  <Box display="flex" alignItems="center" style={{ gap: 10, minWidth: 140 }}>
    <Box
      style={{
        flex: 1,
        minWidth: 80,
        height: 6,
        borderRadius: 3,
        backgroundColor: '#FEE2E2',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          width: `${Math.min(100, Math.max(0, score))}%`,
          height: '100%',
          backgroundColor: COLOR_RED,
          borderRadius: 3,
        }}
      />
    </Box>
    <Typography style={{ fontSize: 12, fontWeight: 600, color: COLOR_RED, minWidth: 32 }}>
      {score}%
    </Typography>
  </Box>
);

const ORG_COLUMNS: Column[] = [
  {
    id: 'client',
    label: 'Clients',
    render: (row) => (
      <span style={{ fontWeight: 600, color: '#2D3748' }}>
        {(row as unknown as OrgRow).client}
      </span>
    ),
  },
  {
    id: 'securityScore',
    label: 'Security score',
    render: (row) => (
      <SecurityScoreBar score={(row as unknown as OrgRow).securityScore} />
    ),
  },
  {
    id: 'endpoints',
    label: 'Endpoints',
    align: 'right',
    render: (row) => (
      <Box style={{ textAlign: 'right', color: '#4A5568' }}>
        {(row as unknown as OrgRow).endpoints}
      </Box>
    ),
  },
  {
    id: 'criticalAlerts',
    label: 'Critical Alerts',
    align: 'right',
    render: (row) => (
      <Box style={{ textAlign: 'right', fontWeight: 600, color: '#2D3748' }}>
        {(row as unknown as OrgRow).criticalAlerts}
      </Box>
    ),
  },
];

const CVE_COLUMNS: Column[] = [
  {
    id: 'cveId',
    label: 'CVE ID',
    render: (row) => (
      <span style={{ fontWeight: 600, color: '#2D3748' }}>
        {(row as unknown as CveRow).cveId}
      </span>
    ),
  },
  {
    id: 'severity',
    label: 'Severity',
    render: (row) => {
      const { severity } = row as unknown as CveRow;
      const style = getSeverityBadgeStyle(severity);
      return (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.25,
            borderRadius: '10px',
            fontSize: 11,
            fontWeight: 600,
            color: style.color,
            bgcolor: style.bg,
          }}
        >
          {severity}
        </Box>
      );
    },
  },
  {
    id: 'affectedEndpoints',
    label: 'Affected Endpoints',
    align: 'right',
    render: (row) => (
      <Box style={{ textAlign: 'right', color: '#4A5568' }}>
        {(row as unknown as CveRow).affectedEndpoints}
      </Box>
    ),
  },
  {
    id: 'title',
    label: 'Title',
    render: (row) => (
      <span style={{ color: '#64748B', fontSize: 12 }}>
        {(row as unknown as CveRow).title || '—'}
      </span>
    ),
  },
];

const ListTableCard: FC<{
  icon: ReactNode;
  title: string;
  columns: Column[];
  rows: TableData[];
  loading?: boolean;
  page: number;
  totalRows: number;
  onPageChange?: (page: number) => void;
}> = ({
  icon,
  title,
  columns,
  rows,
  loading = false,
  page,
  totalRows,
  onPageChange,
}) => (
  <SecurityDashboardCard title={title} icon={icon} fitContent overflow="hidden">
    <Table
      columns={columns}
      data={rows}
      loading={loading}
      showPagination
      compactPagination
      hideRowsPerPage
      page={page}
      rowsPerPage={SOC_PAGE_SIZE}
      totalRows={totalRows}
      onPageChange={onPageChange}
      TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
    />
  </SecurityDashboardCard>
);

const SecurityDashboard: FC = () => {
  const theme = useTheme();
  const { searchInput, setSearchInput } = useSocDashboardPageFilters();
  const { getOrganizationName } = useWazuhScope();
  const [attentionPage, setAttentionPage] = useState(0);
  const [cvesPage, setCvesPage] = useState(0);
  const [incidentPageSize, setIncidentPageSize] = useState(SOC_PAGE_SIZE);
  const dashboardData = useWazuhDashboardData(
    attentionPage,
    0,
    cvesPage,
    SOC_PAGE_SIZE,
    incidentPageSize
  );
  const {
    tripleChartColumns,
    portfolioHealthRowColumns,
    mainWithRailColumns,
    isSmDown,
    isMdDown,
  } = useSecurityDashboardGrid();
  const portfolioKpiColumns = isSmDown
    ? 'minmax(0, 1fr)'
    : isMdDown
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(3, minmax(0, 1fr))';

  const kpiIconColor = theme.palette.primary.main;
  const { loading, portfolioHealth, alertsBySeverity, threatCategories, vulnerabilitiesBySeverity, securityScore, organizationsAttention, recentIncidents, topCves } = dashboardData;

  const myClients = portfolioHealth?.myClients;
  const protectedEndpoints = portfolioHealth?.protectedEndpoints;
  const criticalIncidents = portfolioHealth?.criticalIncidents;
  const protectedIssues = protectedEndpoints?.issues ?? 0;
  const criticalIncidentsTotal = criticalIncidents?.total ?? 0;
  const unresolvedOver48h = criticalIncidents?.unresolvedOver48h ?? 0;

  const alertsLast7 = useMemo(
    () => severitySeriesToCounts(alertsBySeverity?.last7Days ?? []),
    [alertsBySeverity]
  );
  const alertsLast30 = useMemo(
    () => severitySeriesToCounts(alertsBySeverity?.last30Days ?? []),
    [alertsBySeverity]
  );
  const vulnsLast7 = useMemo(
    () => severitySeriesToCounts(vulnerabilitiesBySeverity?.last7Days ?? []),
    [vulnerabilitiesBySeverity]
  );
  const vulnsLast30 = useMemo(
    () => severitySeriesToCounts(vulnerabilitiesBySeverity?.last30Days ?? []),
    [vulnerabilitiesBySeverity]
  );

  const threatLabels = threatCategories?.categories?.map((c) => c.category) ?? [];
  const threatSeries = threatCategories?.categories?.map((c) => c.count) ?? [];
  const threatTotal = threatCategories?.total ?? 0;

  const [orgRows, setOrgRows] = useState<OrgRow[]>([]);
  const [incidentItems, setIncidentItems] = useState<IncidentItem[]>([]);

  useEffect(() => {
    const items = organizationsAttention?.items ?? [];
    let cancelled = false;

    if (items.length === 0) {
      setOrgRows([]);
    } else {
      Promise.all(
        items.map(async (item) => ({
          id: item.organizationId,
          client:
            item.organizationName ??
            (await resolveOrganizationName(item.organizationId, getOrganizationName)),
          securityScore: Math.round(item.securityScore ?? 0),
          endpoints: item.endpointCount ?? 0,
          criticalAlerts: item.criticalAlertCount ?? 0,
        }))
      ).then((rows) => {
        if (!cancelled) setOrgRows(rows);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [organizationsAttention, getOrganizationName]);

  useEffect(() => {
    const items = recentIncidents?.items ?? [];
    let cancelled = false;

    if (items.length === 0) {
      setIncidentItems([]);
    } else {
      Promise.all(
        items.map(async (item, index) => {
          const clientName = item.client
            ?? (item.organizationId
              ? await resolveOrganizationName(item.organizationId, getOrganizationName)
              : '—');
          return {
            id: `${item.wazuhAgentId ?? index}-${item.time}`,
            title: item.incident,
            clientType: 'Client',
            client: clientName,
            endpoint: item.endpoint,
            time: formatRelativeTime(item.time),
          };
        })
      ).then((rows) => {
        if (!cancelled) setIncidentItems(rows);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [recentIncidents, getOrganizationName]);

  const incidentsTotal = recentIncidents?.totalRecords ?? 0;
  const hasMoreIncidents = incidentItems.length < incidentsTotal;

  const cveRows: CveRow[] = useMemo(
    () =>
      (topCves?.items ?? []).map((item) => ({
        id: item.cveId,
        cveId: item.cveId,
        severity: item.severity,
        affectedEndpoints: item.affectedEndpoints,
        title: item.title ?? '',
      })),
    [topCves]
  );

  const impactRows = useMemo(
    () =>
      (securityScore?.impacts ?? []).map((impact, index) => ({
        id: `impact-${index}`,
        label: `${impact.factor}:`,
        impact: formatImpactPercent(impact.impactPercent),
      })),
    [securityScore]
  );

  const securityScoreLabel =
    securityScore?.score != null ? `${securityScore.score}%` : '—';
  const securityComparison =
    securityScore?.vsAverage != null
      ? `${securityScore.vsAverage >= 0 ? '+' : ''}${securityScore.vsAverage} vs avg`
      : '—';

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Security Operations Center')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ minHeight: '100%', boxSizing: 'border-box', gap: 16 }}
      >
        <SecurityDashboardHeader
          searchValue={searchInput}
          onSearchChange={setSearchInput}
        />

        <Box display="flex" flexDirection="column" style={{ gap: 16, minWidth: 0 }}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: portfolioHealthRowColumns,
              gap: 16,
              alignItems: 'stretch',
              minWidth: 0,
            }}
          >
            <SecurityDashboardCard
              title="Portfolio Health"
              icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}
              headerRight={
                <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>
                  Updated: Just now
                </Typography>
              }
              style={{ minWidth: 0, height: '100%' }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: portfolioKpiColumns,
                  py: 2,
                  px: { xs: 1, sm: 0 },
                  gap: { xs: 2, md: 0 },
                }}
              >
                {/* My Clients */}
                <Box
                  sx={{
                    px: 2.5,
                    borderLeft: { xs: 'none', md: 'none' },
                    borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
                    '&:first-of-type': { borderTop: 'none' },
                    '@media (min-width: 900px)': {
                      borderLeft: '1px solid #EDF2F7',
                      '&:first-of-type': { borderLeft: 'none' },
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Typography
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        color: '#A0AEC0',
                        textTransform: 'uppercase',
                      }}
                    >
                      My Clients
                    </Typography>
                    <BusinessCenterIcon style={{ fontSize: 16, color: kpiIconColor }} />
                  </Box>
                  <Typography style={{ fontSize: 26, fontWeight: 700, color: '#1A202C', lineHeight: 1.1 }}>
                    {loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      formatMetricTotal(myClients?.total)
                    )}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 11,
                      color: (myClients?.needAttention ?? 0) > 0 ? COLOR_ORANGE : '#718096',
                      marginTop: 4,
                    }}
                  >
                    {loading
                      ? '—'
                      : `${myClients?.healthy ?? 0} Healthy / ${myClients?.needAttention ?? 0} Need Attention`}
                  </Typography>
                </Box>

                {/* Connected Endpoints */}
                <Box
                  sx={{
                    px: 2.5,
                    borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
                    '@media (min-width: 900px)': {
                      borderLeft: '1px solid #EDF2F7',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Typography
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        color: '#A0AEC0',
                        textTransform: 'uppercase',
                      }}
                    >
                      Connected Endpoints
                    </Typography>
                    <ComputerIcon style={{ fontSize: 16, color: kpiIconColor }} />
                  </Box>
                  <Typography style={{ fontSize: 26, fontWeight: 700, color: '#1A202C', lineHeight: 1.1 }}>
                    {loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      formatMetricTotal(protectedEndpoints?.total)
                    )}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: 11,
                      color: protectedIssues > 0 ? COLOR_ORANGE : '#718096',
                      marginTop: 4,
                    }}
                  >
                    {loading
                      ? '—'
                      : `${(protectedEndpoints?.online ?? 0).toLocaleString()} Online / ${protectedIssues.toLocaleString()} Issues`}
                  </Typography>
                </Box>

                {/* Critical Alerts */}
                <Box
                  sx={{
                    px: 2.5,
                    borderTop: { xs: '1px solid #EDF2F7', md: 'none' },
                    '@media (min-width: 900px)': {
                      borderLeft: '1px solid #EDF2F7',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <Typography
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        color: '#A0AEC0',
                        textTransform: 'uppercase',
                      }}
                    >
                      Critical Alerts
                    </Typography>
                    <NotificationsIcon style={{ fontSize: 16, color: COLOR_RED }} />
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <Typography
                      style={{
                        fontSize: 26,
                        fontWeight: 700,
                        color: criticalIncidentsTotal > 0 ? COLOR_RED : '#1A202C',
                        lineHeight: 1.1,
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={22} />
                      ) : (
                        formatMetricTotal(criticalIncidentsTotal)
                      )}
                    </Typography>
                    <ReportProblemIcon style={{ fontSize: 16, color: COLOR_RED }} />
                  </Box>
                  <Typography
                    style={{
                      fontSize: 11,
                      color: unresolvedOver48h > 0 ? COLOR_RED : '#718096',
                      marginTop: 4,
                    }}
                  >
                    {loading ? '—' : `${unresolvedOver48h} unresolved > 48 hours`}
                  </Typography>
                </Box>
              </Box>
            </SecurityDashboardCard>

            <SecuritySecurityScoreCard
              icon={<ShowChartIcon style={{ fontSize: 18 }} />}
              score={securityScoreLabel}
              comparison={securityComparison}
              impactRows={impactRows}
              style={{ height: '100%' }}
            />
          </Box>

          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: mainWithRailColumns,
              gap: 16,
              alignItems: 'stretch',
              minWidth: 0,
            }}
          >
            <Box display="flex" flexDirection="column" style={{ gap: 16, minWidth: 0 }}>
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: tripleChartColumns,
                  gap: 16,
                  minWidth: 0,
                  alignItems: 'stretch',
                }}
              >
                <SecurityDashboardCard
                  title="Alerts by Severity"
                  icon={<WarningIcon style={{ fontSize: 18 }} />}
                  style={{ minHeight: SECURITY_SEVERITY_CHART_CARD_MIN_HEIGHT, height: '100%' }}
                >
                  <SecuritySeverityCompareBarChart
                    last30Days={alertsLast30.length ? alertsLast30 : DEFAULT_SEVERITY_COUNTS}
                    last7Days={alertsLast7.length ? alertsLast7 : DEFAULT_SEVERITY_COUNTS}
                    fill
                  />
                </SecurityDashboardCard>

                <SecurityDashboardCard
                  title="Top Threat Categories"
                  icon={<ReportProblemIcon style={{ fontSize: 18 }} />}
                  style={{ minHeight: SECURITY_SEVERITY_CHART_CARD_MIN_HEIGHT, height: '100%' }}
                >
                  <Box style={{ flex: '1 1 auto', minHeight: 0, width: '100%' }}>
                    <ThreatDonutChart
                      labels={threatLabels}
                      series={threatSeries}
                      total={threatTotal}
                      loading={loading}
                    />
                  </Box>
                </SecurityDashboardCard>

                <SecurityDashboardCard
                  title="Open Vulnerabilities"
                  icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
                  style={{ minHeight: SECURITY_SEVERITY_CHART_CARD_MIN_HEIGHT, height: '100%' }}
                >
                  <SecuritySeverityCompareBarChart
                    last30Days={vulnsLast30.length ? vulnsLast30 : DEFAULT_SEVERITY_COUNTS}
                    last7Days={vulnsLast7.length ? vulnsLast7 : DEFAULT_SEVERITY_COUNTS}
                    fill
                  />
                </SecurityDashboardCard>
              </Box>

              <ListTableCard
                title="Top CVEs"
                icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
                columns={CVE_COLUMNS}
                rows={cveRows as unknown as TableData[]}
                loading={loading}
                page={cvesPage}
                totalRows={topCves?.totalRecords ?? cveRows.length}
                onPageChange={setCvesPage}
              />

              <ListTableCard
                title="Organizations Requiring Attention"
                icon={<BusinessCenterIcon style={{ fontSize: 18 }} />}
                columns={ORG_COLUMNS}
                rows={orgRows as unknown as TableData[]}
                loading={loading}
                page={attentionPage}
                totalRows={organizationsAttention?.totalRecords ?? orgRows.length}
                onPageChange={setAttentionPage}
              />
            </Box>

            <SecurityDashboardCard
              title="Recent Critical Incidents"
              icon={<HistoryIcon style={{ fontSize: 18 }} />}
              headerRight={<TuneIcon style={{ fontSize: 16, color: '#A0AEC0', cursor: 'pointer' }} />}
              style={{ minHeight: 420, height: '100%', alignSelf: 'stretch' }}
              footer={(
                hasMoreIncidents ? (
                  <Typography
                    onClick={() => setIncidentPageSize((prev) => prev + SOC_PAGE_SIZE)}
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.4px',
                      color: COLOR_BLUE,
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                    }}
                  >
                    VIEW MORE
                  </Typography>
                ) : null
              )}
            >
              <Box
                display="flex"
                flexDirection="column"
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <Box>
                  {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : incidentItems.length === 0 ? (
                    <Typography style={{ fontSize: 12, color: '#94A3B8', padding: 16 }}>
                      No recent critical incidents
                    </Typography>
                  ) : (
                    incidentItems.map((item, index) => (
                      <IncidentListItem
                        key={item.id}
                        item={item}
                        isLast={index === incidentItems.length - 1}
                      />
                    ))
                  )}
                </Box>
                <Box flex={1} minHeight={16} />
              </Box>
            </SecurityDashboardCard>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SecurityDashboard;
